<template>
  <transition name="panel-slide">
    <div v-if="show" class="signal-overlay" @click.self="$emit('close')">
      <aside class="signal-panel" role="dialog" aria-label="Priority Signal Engine">
        <header class="signal-header">
          <div>
            <h4 class="signal-title">Priority Signals</h4>
            <p class="signal-subtitle">{{ contextLabel }}</p>
          </div>
          <button type="button" class="close-btn" @click="$emit('close')" aria-label="Close">
            <b-icon icon="x" font-scale="1.2"></b-icon>
          </button>
        </header>

          <div class="signal-context">
            <b-icon icon="info-circle" class="mr-1"></b-icon>
            <span>{{ contextSummary }}</span>
          </div>

        <div class="signal-body">
          <div v-if="loading" class="state-block">
            <b-spinner small variant="success"></b-spinner>
            <p>Loading signal for selected indicator…</p>
          </div>

          <div v-else-if="errorMessage" class="state-block state-error">
            <b-icon icon="exclamation-triangle" variant="warning"></b-icon>
            <h6>Could not load signals</h6>
            <p>{{ errorMessage }}</p>
            <b-button size="sm" variant="outline-success" @click="fetchInsights">Try again</b-button>
          </div>

          <div v-else-if="insights.length === 0" class="state-block">
            <b-icon icon="inbox" class="state-icon"></b-icon>
            <h6>Select an indicator</h6>
            <p>Choose an indicator in the control panel, then open Priority Signals to analyze that selection.</p>
          </div>

          <ol v-else class="signal-list">
            <li
              v-for="(insight, index) in insights"
              :key="insight.id || index"
              class="signal-item"
            >
              <div class="signal-item-top">
                <span class="signal-rank">{{ index + 1 }}</span>
                <span class="severity-pill" :class="insight.type">{{ severityLabel(insight.type) }}</span>
                <span v-if="insight.is_selected" class="selected-pill">Your selection</span>
                <span v-if="insight.state" class="signal-location">{{ insight.state }}</span>
              </div>

              <h6 class="signal-indicator">{{ insight.indicator || cleanTitle(insight.title) }}</h6>

              <p class="signal-change">{{ insight.description }}</p>

              <div class="signal-action">
                <span class="action-label">Suggested action</span>
                <p>{{ insight.recommendation }}</p>
              </div>
            </li>
          </ol>
        </div>

        <footer class="signal-footer">
          <span class="footer-meta">
            MSDAT Priority Signal Engine{{ lastUpdatedLabel }}
          </span>
          <button
            type="button"
            class="refresh-btn"
            @click="fetchInsights"
            :disabled="loading"
            title="Refresh signals"
          >
            <b-icon icon="arrow-clockwise" :class="{ spin: loading }"></b-icon>
          </button>
        </footer>
      </aside>
    </div>
  </transition>
</template>

<script>
import apiServices from '@/modules/data-layer/services/ApiServices';

export default {
  name: 'AIInsightsPanel',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    filters: {
      type: Object,
      default: () => ({
        indicator: 'N/A',
        location: 'Nigeria',
        period: 'N/A',
        datasource: 'N/A',
      }),
    },
  },
  data() {
    return {
      loading: false,
      insights: [],
      errorMessage: '',
      lastUpdated: null,
      requestToken: 0,
    };
  },
  computed: {
    contextLabel() {
      return this.filters.location || 'National';
    },
    contextSummary() {
      const selected = this.filters.indicatorId
        ? `Focused only on ${this.filters.indicator || 'your selected indicator'}`
        : 'Select an indicator in the control panel to analyze';
      const parts = [
        selected,
        this.filters.location ? `in ${this.filters.location}` : '',
        this.filters.datasource && this.filters.datasource !== 'N/A' ? `via ${this.filters.datasource}` : '',
      ].filter(Boolean);
      return `${parts.join(' ')}.`;
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) return '';
      const time = this.lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return ` · ${time}`;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.fetchInsights();
      } else {
        this.resetPanel();
      }
    },
    filters: {
      deep: true,
      handler(newFilters, oldFilters) {
        if (!this.show) return;
        const nextId = newFilters && newFilters.indicatorId;
        const prevId = oldFilters && oldFilters.indicatorId;
        if (String(nextId) !== String(prevId)) {
          this.fetchInsights();
        }
      },
    },
  },
  methods: {
    severityLabel(type) {
      if (type === 'critical') return 'High priority';
      if (type === 'warning') return 'Watch';
      return 'Stable';
    },
    cleanTitle(title) {
      if (!title) return 'Indicator';
      return title.replace(/^(Significant Spike|Critical Drop|Trend Shift|Stable|Current reading|Focus):\s*/i, '');
    },
    resetPanel() {
      this.requestToken += 1;
      this.insights = [];
      this.errorMessage = '';
      this.loading = false;
      this.lastUpdated = null;
    },
    async fetchInsights() {
      const requestId = this.requestToken + 1;
      this.requestToken = requestId;
      this.loading = true;
      this.errorMessage = '';
      this.insights = []; // never keep a previous indicator's signals on screen

      const activeFilters = { ...(this.filters || {}) };
      const selectedId = activeFilters.indicatorId;

      if (!selectedId) {
        if (requestId === this.requestToken) {
          this.loading = false;
          this.insights = [];
        }
        return;
      }

      try {
        const response = await apiServices.getAIInsights(activeFilters);
        if (requestId !== this.requestToken) return; // ignore stale responses

        const rows = Array.isArray(response.data) ? response.data : [];
        this.insights = rows
          .filter((item) => String(item.id) === String(selectedId))
          .slice(0, 1);
        this.lastUpdated = new Date();
      } catch (error) {
        if (requestId !== this.requestToken) return;
        console.error('Failed to fetch AI insights:', error);
        this.insights = [];
        const apiMessage = error.response && error.response.data && error.response.data.message;
        this.errorMessage = apiMessage || error.message || 'Failed to load priority signals. Please try again.';
      } finally {
        if (requestId === this.requestToken) {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.signal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}

.signal-panel {
  width: 380px;
  max-width: 100%;
  height: 100%;
  background: #fff;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
}

.signal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px 18px;
  background: #007d53;
  color: #fff;
  flex-shrink: 0;
}

.signal-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.signal-subtitle {
  margin: 4px 0 0;
  font-size: 0.8rem;
  opacity: 0.9;
}

.close-btn {
  background: transparent;
  border: none;
  color: #fff;
  padding: 0;
  cursor: pointer;
  line-height: 1;
}

.signal-context {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding: 10px 16px;
  font-size: 0.75rem;
  line-height: 1.45;
  color: #4a5568;
  background: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}

.signal-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px 16px;
  background: #f8f9fa;
}

.state-block {
  text-align: center;
  padding: 32px 16px;
  color: #4a5568;

  h6 {
    margin: 12px 0 6px;
    font-weight: 700;
    color: #2d3748;
  }

  p {
    margin: 0 0 12px;
    font-size: 0.85rem;
    line-height: 1.5;
  }

  .state-icon {
    font-size: 1.75rem;
    color: #a0aec0;
  }

  &.state-error h6 {
    color: #c53030;
  }
}

.signal-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.signal-item {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.signal-item-top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.signal-rank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #edf2f7;
  color: #4a5568;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
}

.severity-pill {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 4px;

  &.critical {
    background: #fff5f5;
    color: #c53030;
    border: 1px solid #feb2b2;
  }

  &.warning {
    background: #fffaf0;
    color: #c05621;
    border: 1px solid #fbd38d;
  }

  &.positive {
    background: #f0fff4;
    color: #276749;
    border: 1px solid #9ae6b4;
  }
}

.selected-pill {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 2px 8px;
  border-radius: 4px;
  background: #ebf8ff;
  color: #2b6cb0;
  border: 1px solid #bee3f8;
}

.signal-location {
  margin-left: auto;
  font-size: 0.7rem;
  color: #718096;
}

.signal-indicator {
  margin: 0 0 6px;
  font-size: 0.92rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1.35;
}

.signal-change {
  margin: 0 0 10px;
  font-size: 0.84rem;
  color: #4a5568;
  line-height: 1.5;
}

.signal-action {
  padding-top: 10px;
  border-top: 1px solid #edf2f7;

  .action-label {
    display: block;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #007d53;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    font-size: 0.8rem;
    color: #2d3748;
    line-height: 1.45;
  }
}

.signal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-top: 1px solid #e2e8f0;
  background: #fff;
  flex-shrink: 0;
}

.footer-meta {
  font-size: 0.7rem;
  color: #718096;
}

.refresh-btn {
  background: none;
  border: none;
  color: #007d53;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  line-height: 1;

  &:hover {
    background: #f0fff4;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.panel-slide-enter-active,
.panel-slide-leave-active {
  transition: opacity 0.2s ease;
}

.panel-slide-enter-active .signal-panel,
.panel-slide-leave-active .signal-panel {
  transition: transform 0.25s ease;
}

.panel-slide-enter,
.panel-slide-leave-to {
  opacity: 0;
}

.panel-slide-enter .signal-panel,
.panel-slide-leave-to .signal-panel {
  transform: translateX(100%);
}

@media (max-width: 576px) {
  .signal-panel {
    width: 100%;
  }

  .signal-header {
    padding: 14px 16px;
  }

  .signal-body {
    padding: 10px 12px 14px;
  }

  .signal-location {
    margin-left: 0;
    width: 100%;
  }
}
</style>
